import api from './api';

export interface Permission {
  id: string;
  name: string;
  description: string;
  module: string;
  action: string;
  isActive: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

export interface Role {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PermissionsByModule {
  [module: string]: Permission[];
}

export interface RolePermissionsResponse {
  role: Role;
  permissions: Permission[];
}

class PermissionsService {
  /**
   * Get all active permissions
   */
  async getAllPermissions(): Promise<Permission[]> {
    const response = await api.get<Permission[]>('/permissions');
    return response.data;
  }

  /**
   * Get permissions grouped by module
   */
  async getPermissionsByModule(): Promise<PermissionsByModule> {
    const response = await api.get<PermissionsByModule>('/permissions/by-module');
    return response.data;
  }

  /**
   * Get all roles
   */
  async getRoles(): Promise<Role[]> {
    const response = await api.get<Role[]>('/roles');
    return response.data;
  }

  /**
   * Get permissions for a specific role
   */
  async getRolePermissions(roleId: string): Promise<RolePermissionsResponse> {
    const response = await api.get<RolePermissionsResponse>(`/permissions/roles/${roleId}`);
    return response.data;
  }

  /**
   * Assign permissions to a role (replaces existing)
   */
  async assignPermissionsToRole(roleId: string, permissionIds: string[]): Promise<RolePermissionsResponse> {
    const response = await api.post<RolePermissionsResponse>(
      `/permissions/roles/${roleId}/assign`,
      { permissionIds }
    );
    return response.data;
  }

  /**
   * Add permissions to a role (adds to existing)
   */
  async addPermissionsToRole(roleId: string, permissionIds: string[]): Promise<RolePermissionsResponse> {
    const response = await api.post<RolePermissionsResponse>(
      `/permissions/roles/${roleId}/add`,
      { permissionIds }
    );
    return response.data;
  }

  /**
   * Remove permissions from a role
   */
  async removePermissionsFromRole(roleId: string, permissionIds: string[]): Promise<RolePermissionsResponse> {
    const response = await api.delete<RolePermissionsResponse>(
      `/permissions/roles/${roleId}/remove`,
      { data: { permissionIds } }
    );
    return response.data;
  }

  /**
   * Update a permission
   */
  async updatePermission(
    permissionId: string,
    data: { description?: string; isActive?: boolean; priority?: number }
  ): Promise<Permission> {
    const response = await api.patch<Permission>(`/permissions/${permissionId}`, data);
    return response.data;
  }
}

const permissionsServiceInstance = new PermissionsService();
export default permissionsServiceInstance;

